export function useConditions() {
  function evaluateSingleCondition(answer, operator, value) {
    switch (operator) {
      case 'equals':       return String(answer ?? '') === String(value ?? '')
      case 'not_equals':   return String(answer ?? '') !== String(value ?? '')
      case 'contains':
        return Array.isArray(answer)
          ? answer.map(String).includes(String(value))
          : String(answer ?? '').includes(String(value ?? ''))
      case 'not_contains':
        return Array.isArray(answer)
          ? !answer.map(String).includes(String(value))
          : !String(answer ?? '').includes(String(value ?? ''))
      case 'greater_than':          return numericCompare(answer, value, (a, b) => a > b)
      case 'less_than':             return numericCompare(answer, value, (a, b) => a < b)
      case 'greater_than_or_equal': return numericCompare(answer, value, (a, b) => a >= b)
      case 'less_than_or_equal':    return numericCompare(answer, value, (a, b) => a <= b)
      // Date strings (YYYY-MM-DD), time (HH:MM), and datetime-local (YYYY-MM-DDTHH:MM) all
      // sort correctly as plain strings. For date_range answers ("start,end"), compare
      // against the start part for 'before' and the end part for 'after'.
      case 'before': {
        if (!answer || !value) return false
        const a = String(answer).includes(',') ? String(answer).split(',')[0] : String(answer)
        return a !== '' && a < String(value)
      }
      case 'after': {
        if (!answer || !value) return false
        const parts = String(answer).split(',')
        const a = parts.length > 1 ? (parts[1] || parts[0]) : String(answer)
        return a !== '' && a > String(value)
      }
      case 'is_answered':  return answer !== null && answer !== undefined && answer !== ''
      case 'is_blank':     return answer === null || answer === undefined || answer === ''
      default:             return false
    }
  }

  function numericCompare(answer, value, fn) {
    const a = Number(answer)
    const b = Number(value)
    if (isNaN(a) || isNaN(b)) return false
    return fn(a, b)
  }

  // Returns the resolved action string for a given question and current answers dict.
  // answers is keyed by event_question.id (string).
  // Possible return values: 'show' | 'hide' | 'require' | 'block' | 'redirect'
  // A question with no rules always returns 'show'.
  function evaluateConditions(question, answers) {
    const cond = question.conditions
    if (!cond || !cond.rules || cond.rules.length === 0) return 'show'

    const results = cond.rules.map(rule =>
      evaluateSingleCondition(answers[rule.source_event_question_id], rule.operator, rule.value)
    )

    const conditionMet = cond.match === 'all'
      ? results.every(Boolean)
      : results.some(Boolean)

    const action = cond.action || 'show'

    if (conditionMet) return action
    return action === 'show' ? 'hide' : 'show'
  }

  function isQuestionVisible(question, answers) {
    return evaluateConditions(question, answers) !== 'hide'
  }

  function isQuestionRequired(question, answers, baseRequired) {
    const action = evaluateConditions(question, answers)
    return baseRequired || action === 'require'
  }

  function hasConditions(question) {
    return question.conditions?.rules?.length > 0
  }

  // Phase 3a — choice types
  // Phase 3b — text, numeric, date, presence-only types
  // hidden is excluded — never shown to booker so can't be a trigger source
  const OPERATORS_FOR_TYPE = {
    drop_down:    ['equals', 'not_equals', 'is_answered', 'is_blank'],
    radio_button: ['equals', 'not_equals', 'is_answered', 'is_blank'],
    check_box:    ['contains', 'not_contains', 'is_answered', 'is_blank'],
    boolean:      ['equals', 'is_answered', 'is_blank'],
    one_line:     ['equals', 'not_equals', 'contains', 'not_contains', 'is_answered', 'is_blank'],
    multi_line:   ['equals', 'not_equals', 'contains', 'not_contains', 'is_answered', 'is_blank'],
    number:       ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_answered', 'is_blank'],
    rating:       ['equals', 'greater_than', 'less_than', 'is_answered', 'is_blank'],
    date:         ['before', 'after', 'is_answered', 'is_blank'],
    email:        ['is_answered', 'is_blank'],
    phone_number: ['is_answered', 'is_blank'],
    url:          ['is_answered', 'is_blank'],
    file_upload:  ['is_answered', 'is_blank'],
  }

  const OPERATOR_LABELS = {
    equals:                 'equals',
    not_equals:             'does not equal',
    contains:               'contains',
    not_contains:           'does not contain',
    greater_than:           'is greater than',
    less_than:              'is less than',
    greater_than_or_equal:  'is greater than or equal to',
    less_than_or_equal:     'is less than or equal to',
    before:                 'is before',
    after:                  'is after',
    is_answered:            'is answered',
    is_blank:               'is blank',
  }

  const ACTION_LABELS = {
    show:     'Show this question',
    hide:     'Hide this question',
    require:  'Make this question required',
    block:    'Block the booking',
    redirect: 'Redirect to URL',
  }

  function operatorsForType(questionType) {
    return OPERATORS_FOR_TYPE[questionType] ?? []
  }

  function canBeSource(questionType) {
    return questionType in OPERATORS_FOR_TYPE
  }

  // Returns the appropriate value-input type for a given question type
  // 'options' — render the source question's options list
  // 'boolean' — render Yes / No
  // 'number'  — render a number input
  // 'date'    — render a date input
  // 'text'    — render a plain text input
  // 'none'    — no value input needed (is_answered / is_blank)
  // questionDetails is optional — pass the source question's question_details to get
  // the correct value-input type for date sub-modes (time, datetime, date_range).
  function valueInputType(questionType, operator, questionDetails = {}) {
    if (operator === 'is_answered' || operator === 'is_blank') return 'none'
    switch (questionType) {
      case 'drop_down':
      case 'radio_button':
      case 'check_box':    return 'options'
      case 'boolean':      return 'boolean'
      case 'number':
      case 'rating':       return 'number'
      case 'date': {
        const mode = questionDetails?.date_mode || 'date'
        if (mode === 'time')     return 'time'
        if (mode === 'datetime') return 'datetime'
        return 'date'  // 'date' and 'date_range' both compare against a single date value
      }
      default:             return 'text'
    }
  }

  return {
    evaluateConditions,
    evaluateSingleCondition,
    isQuestionVisible,
    isQuestionRequired,
    hasConditions,
    operatorsForType,
    canBeSource,
    valueInputType,
    OPERATOR_LABELS,
    ACTION_LABELS,
  }
}
